import { DatePipe, TitleCasePipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { forkJoin, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ScheduledType } from 'src/app/models/scheduled-type.model';
import { Status } from 'src/app/models/status.model';
import { Roles, isOperator } from 'src/app/auth/roles';
import { LogsheetService } from 'src/app/services/logsheet/logsheet.service';
import { OverviewService } from 'src/app/services/overview/overview.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { ErrorHandlerService } from 'src/app/services/errorHandler/error-handler.service';
import { AvailableWorkflow, WorkflowRuntimeListItem, WorkflowService } from 'src/app/services/workflow/workflow.service';
import { RefreshStateService } from 'src/app/services/refresh-state/refresh-state.service';
import {
  StageSegment,
  StatusToken,
  overdueLabel,
  stageProgressLabel,
  stageSegments,
  statusText,
  statusToken
} from 'src/app/components/workflow-card/workflow-card.helpers';

interface NonAdminHomeRefreshState {
  activeTab: 'workflows' | 'forms';
  workflowFilter: 'tasks' | 'start' | 'history';
  historySearch: string;
  historyStatusFilter: 'all' | 'inprogress' | 'completed' | 'rejected';
  workflowSort: 'newest' | 'oldest';
  workflowPage: number;
  currentPage: number;
  logSheetType: ScheduledType;
  selectedStatuses: Status[];
  selectedDateRange: string;
  startDate: string;
  endDate: string;
  statusSectionOpen: boolean;
  isSearchOpen: boolean;
  searchQuery: string;
  searchPage: number;
}

@Component({
  standalone: false,
  selector: 'app-non-admin-home',
  templateUrl: './non-admin-home.component.html',
  styleUrls: ['../../components/workflow-card/workflow-card.css', './non-admin-home.component.css'],
  providers: [TitleCasePipe],
})
export class NonAdminHomeComponent implements OnInit, OnDestroy {
	readonly Roles = Roles;
  activeTab: 'workflows' | 'forms' = 'workflows';
  needsMyAction: WorkflowRuntimeListItem[] = [];
  availableToStart: AvailableWorkflow[] = [];
  workflowsLoading = false;
  workflowError = '';
  showHistory = false;
  workflowFilter: 'tasks' | 'start' | 'history' = 'tasks';
  historyItems: WorkflowRuntimeListItem[] = [];
  historyLoading = false;
  historyError = '';
  private routerSubscription: Subscription = new Subscription();
  private readonly menuResetSubscription = new Subscription();
  ScheduledType = ScheduledType;
  Status = Status;
  username: string | null = "";
  today: string | null = "";
  currentPage: number = 1;
  pageSize: number = 12;
  currentIncrement: number = 12;
  logSheetType: ScheduledType = ScheduledType.none;
  selectedStatuses: Status[] = [];
  selectedDateRange: string = 'allTime';
  startDate: string = "";
  endDate: string = "";
  displayFilterOverlay: boolean = false;
  logs: any[] = [];
  filteredLogs: any[] | undefined;
  currentUser: any;
  role: any;
  isExpired = false;
  totalPages: number = 0;
  loopArray: Array<number> = new Array();
  selectedPage = {
    backgroundColor: '#0F2642',
    color: 'white',
    borderRadius: '4px',
  }

  statusSectionOpen: boolean = true;

  statusOptions = [
    { value: Status.executed, label: 'Done' },
    { value: Status.overdue, label: 'Overdue' },
    { value: Status.inProgress, label: 'In Progress' },
    { value: Status.available, label: 'Available' },
    { value: Status.upcoming, label: 'Upcoming' },
    { value: Status.incomplete, label: 'Incomplete' }
  ];

  dateRangeOptions = [
    { value: 'allTime', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'thisWeek', label: 'This Week' },
    { value: 'thisMonth', label: 'This Month' }
  ];

  filterPulseActive: boolean = false;
  private filterPulseTimer: any = null;

  get hasActiveFilters(): boolean {
    return this.selectedStatuses.length > 0 || this.selectedDateRange !== 'allTime';
  }

  get activeFilterCount(): number {
    return this.selectedStatuses.length + (this.selectedDateRange !== 'allTime' ? 1 : 0);
  }

  hoveredUsername: string | null = null;
  isSearchOpen: boolean = false;
  searchQuery: string = '';
  searchResults: any[] | null = null;
  allLogsCache: any[] = [];
  searchLoading: boolean = false;
  searchPage: number = 1;
  private searchDebounceTimer: any = null;

  get activeTotalPages(): number {
    if (this.searchResults !== null) {
      return Math.max(1, Math.ceil(this.searchResults.length / this.pageSize));
    }
    return this.totalPages;
  }

  get activeLoopArray(): number[] {
    return new Array(this.activeTotalPages).fill(0).map((_, i) => i + 1);
  }

  get activeCurrentPage(): number {
    return this.searchResults !== null ? this.searchPage : this.currentPage;
  }

  get pagedSearchResults(): any[] {
    if (this.searchResults === null) return [];
    const start = (this.searchPage - 1) * this.pageSize;
    return this.searchResults.slice(start, start + this.pageSize);
  }

  isTruncated(element: HTMLElement): boolean {
    return element.scrollWidth > element.clientWidth;
  }

  constructor(
    public overviewService: OverviewService,
    private logSheetService: LogsheetService,
    public toastService: ToastService,
    private titleCasePipe: TitleCasePipe,
    private router: Router,
    private datePipe: DatePipe,
    private workflowService: WorkflowService,
    private errorService: ErrorHandlerService,
    private refreshState: RefreshStateService
  ) { }

  ngOnInit(): void {
    this.currentUser = localStorage.getItem('email');
    this.role = localStorage.getItem('role');
    this.menuResetSubscription.add(
      this.refreshState.areaReset$.subscribe(area => {
        if (area === 'dashboard') this.resetToMenuHead();
      }),
    );
    //Store username to be displayed on non-admin home page
    this.username = localStorage.getItem('firstName') + " " + localStorage.getItem('lastName');
    //Store current Date
    this.today = new Intl.DateTimeFormat('en-US',
      {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }
    ).format(new Date());
    this.restoreRefreshState();
    this.loadAvailableWorkflows();
    if (this.workflowFilter === 'history') {
      this.loadHistory();
    }
    this.retrieveAllLogSheets(true);
    if (this.isSearchOpen && this.searchQuery.trim()) {
      this.fetchAllLogsForSearch();
    }

    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        if (event.urlAfterRedirects === '/dashboard' || event.url === '/dashboard') {
          this.retrieveAllLogSheets(true);
        }
      });
  }

  setActiveTab(tab: 'workflows' | 'forms') {
    this.activeTab = tab;
    if (tab === 'workflows' && this.needsMyAction.length === 0 && this.availableToStart.length === 0) {
      this.loadMyWork();
    }
    this.persistRefreshState();
  }

  loadMyWork() {
    this.workflowsLoading = true;
    this.workflowError = '';
    this.workflowService.getMyWork().subscribe({
      next: (response) => {
        this.needsMyAction = (response.needsMyAction || []).filter(item => !this.isTerminalWorkflowItem(item));
        this.availableToStart = response.availableToStart || [];
        this.workflowPage = Math.min(this.workflowPage, this.totalCardPages(this.activeWorkflowCount));
        this.workflowsLoading = false;
      },
      error: (err) => {
        this.workflowsLoading = false;
        this.workflowError = this.errorService.handleRetrievalErrors(err)?.message || 'Unable to load workflows';
      }
    });
  }

  loadAvailableWorkflows() {
    this.loadMyWork();
  }

  loadHistory() {
    this.showHistory = true;
    this.historyLoading = true;
    this.historyError = '';
    this.workflowService.getWorkflowHistory().subscribe({
      next: (items) => {
        this.historyItems = items || [];
        this.historyLoading = false;
      },
      error: (err) => {
        this.historyLoading = false;
        this.historyError = this.errorService.handleRetrievalErrors(err)?.message || 'Unable to load workflow history';
      }
    });
  }

  hideHistory() {
    this.showHistory = false;
  }

  /** Switches the workflow view (My Tasks / Start New / History) via the filter tabs. */
  setWorkflowFilter(filter: 'tasks' | 'start' | 'history') {
    this.workflowFilter = filter;
    this.workflowPage = 1;
    if (filter === 'history') {
      this.loadHistory();
    } else {
      this.showHistory = false;
    }
    this.persistRefreshState();
  }

  /* ---- Client-side pagination for the workflow card lists ---- */
  cardPageSize = 9;
  workflowPage = 1;

  private paginate<T>(items: T[], page: number): T[] {
    const start = (page - 1) * this.cardPageSize;
    return items.slice(start, start + this.cardPageSize);
  }

  totalCardPages(count: number): number {
    return Math.max(1, Math.ceil((count || 0) / this.cardPageSize));
  }

  cardPageList(count: number): number[] {
    return Array.from({ length: this.totalCardPages(count) }, (_, i) => i + 1);
  }

  /** Scheduled workflows that are due — they surface under My Tasks because they need attention now. */
  get scheduledAvailable(): AvailableWorkflow[] {
    return this.availableToStart.filter((w) => w.isScheduled);
  }
  /** On-demand workflows — startable anytime, so they live under New workflow. */
  get onDemandAvailable(): AvailableWorkflow[] {
    return this.availableToStart.filter((w) => !w.isScheduled);
  }

  /** Item count for the currently visible workflow tab, used to size pagination. */
  get activeWorkflowCount(): number {
    if (this.workflowFilter === 'tasks') return this.needsMyAction.length;
    if (this.workflowFilter === 'start') return this.onDemandAvailable.length;
    return this.filteredHistoryItems().length;
  }

  setWorkflowPage(page: number) {
    this.workflowPage = Math.min(Math.max(1, page), this.totalCardPages(this.activeWorkflowCount));
    this.persistRefreshState();
  }

  get pagedNeedsMyAction(): WorkflowRuntimeListItem[] {
    // #3: My Tasks honours the same submission-date ordering as History (newest first by default).
    return this.paginate(this.sortBySubmission(this.needsMyAction), this.workflowPage);
  }

  get pagedAvailableToStart(): AvailableWorkflow[] {
    return this.paginate(this.onDemandAvailable, this.workflowPage);
  }

  /* ---- History search (debounced) + status filter ---- */
  historySearch = '';
  historyStatusFilter: 'all' | 'inprogress' | 'completed' | 'rejected' = 'all';
  historyFilterOpen = false;
  historyStatusOptions: { value: 'all' | 'inprogress' | 'completed' | 'rejected'; label: string }[] = [
    { value: 'all', label: 'All statuses' },
    { value: 'inprogress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'rejected', label: 'Rejected' },
  ];
  private historySearchTimer: any = null;

  /** Debounce the history search so we only re-filter ~300ms after typing stops. */
  onHistorySearch(value: string) {
    if (this.historySearchTimer) clearTimeout(this.historySearchTimer);
    this.historySearchTimer = setTimeout(() => {
      this.historySearch = (value || '').toLowerCase().trim();
      this.workflowPage = 1;
      this.persistRefreshState();
    }, 300);
  }

  toggleHistoryFilter() {
    this.historyFilterOpen = !this.historyFilterOpen;
  }

  setHistoryStatusFilter(filter: 'all' | 'inprogress' | 'completed' | 'rejected') {
    this.historyStatusFilter = filter;
    this.historyFilterOpen = false;
    this.workflowPage = 1;
    this.persistRefreshState();
  }

  /** Status bucket key for an item, used by both grouping and the status filter. */
  private historyBucket(item: WorkflowRuntimeListItem): 'inprogress' | 'completed' | 'rejected' {
    const status = (item.status || '').toString().trim().toUpperCase();
    return status === 'COMPLETED' ? 'completed' : status === 'REJECTED' ? 'rejected' : 'inprogress';
  }

  /** Whether an item matches the current debounced search query. */
  private matchesHistorySearch(item: WorkflowRuntimeListItem): boolean {
    if (!this.historySearch) return true;
    const haystack = [
      item.name,
      item.summaryTitle,
      'ref #' + item.workflowItemId,
      ...(item.summaryFields || []).map((f) => f.value)
    ].join(' ').toLowerCase();
    return haystack.includes(this.historySearch);
  }

  /** History items narrowed by the active search query and status filter (flat, ungrouped). */
  /* ---- #3: sort submitted-form cards by submission date (newest/oldest) ---- */
  workflowSort: 'newest' | 'oldest' = 'newest';

  toggleWorkflowSort() {
    this.workflowSort = this.workflowSort === 'newest' ? 'oldest' : 'newest';
    this.workflowPage = 1;
    this.persistRefreshState();
  }

  /** Order a list of workflow items by submission date per the active sort direction. */
  private sortBySubmission(items: WorkflowRuntimeListItem[]): WorkflowRuntimeListItem[] {
    const dir = this.workflowSort === 'newest' ? -1 : 1;
    return [...items].sort((a, b) => {
      const ta = new Date(a.startedAt || 0).getTime();
      const tb = new Date(b.startedAt || 0).getTime();
      return (ta - tb) * dir;
    });
  }

  private filteredHistoryItems(): WorkflowRuntimeListItem[] {
    const filtered = this.historyItems.filter((item) => {
      const key = this.historyBucket(item);
      if (this.historyStatusFilter !== 'all' && this.historyStatusFilter !== key) return false;
      return this.matchesHistorySearch(item);
    });
    return this.sortBySubmission(filtered);
  }

  /** Current page of filtered history as a single flat list (no status grouping). */
  get pagedHistoryItems(): WorkflowRuntimeListItem[] {
    return this.paginate(this.filteredHistoryItems(), this.workflowPage);
  }

  /** #N1: the backend stores UTC; parse timezone-less timestamps as UTC, not local, otherwise
   *  every recent item reads as "Just now" (local parse lands ~tz-offset in the future). */
  private asUtcDate(value: string): Date {
    const normalized = /[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : value + 'Z';
    return new Date(normalized);
  }

  /** Compact "time ago" for the card footer. */
  relativeTime(value?: string | null): string {
    if (!value) return '';
    const then = this.asUtcDate(value).getTime();
    if (isNaN(then)) return '';
    const mins = Math.floor((Date.now() - then) / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return this.asUtcDate(value).toLocaleDateString();
  }

  /** Short, human date e.g. "15 Jun 2026" for the submitted line. */
  formatShort(value?: string | null): string {
    if (!value) return '';
    const d = this.asUtcDate(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  }

  /** Specific, human label for the status chip (Returned / Needs approval / Awaiting / …). */
  pillLabel(item: WorkflowRuntimeListItem): string {
    return statusText(item);
  }

  /** The single primary status this card resolves to, from a closed vocabulary. */
  statusToken(item: WorkflowRuntimeListItem): StatusToken {
    return statusToken(item);
  }

  /** Overdue is a secondary condition on the current stage; empty string means on time. */
  overdueLabel(item: WorkflowRuntimeListItem): string {
    return overdueLabel(item);
  }

  stageSegments(item: WorkflowRuntimeListItem): StageSegment[] {
    return stageSegments(item);
  }

  stageProgressLabel(item: WorkflowRuntimeListItem): string {
    return stageProgressLabel(item);
  }

  /** Workflow-specific summary values, capped so the facts grid can't grow unbounded. */
  summaryMetaFields(item: WorkflowRuntimeListItem): { label: string; value: string }[] {
    return (item.summaryFields || []).slice(0, 3);
  }

  /** One utterance for screen readers instead of a dozen disconnected fragments. */
  cardAriaLabel(item: WorkflowRuntimeListItem): string {
    return [
      item.name,
      item.summaryTitle || '',
      this.pillLabel(item),
      this.stageProgressLabel(item),
      this.overdueLabel(item)
    ]
      .filter(Boolean)
      .join(', ');
  }

  /** Whether this item is the initiator's to act on after a return. */
  isReturned(item: WorkflowRuntimeListItem): boolean {
    return (item.lastAction || '').toString().trim().toUpperCase() === 'RETURNED';
  }

  /** The approval path a workflow routes through, derived from its stage count. */
  stageFlow(count?: number): string[] {
    const n = count || 0;
    if (n <= 0) return [];
    const labels = ['You submit'];
    for (let i = 2; i <= n; i++) labels.push(`Approver ${i - 1}`);
    return labels;
  }

  openWorkflow(workflow: AvailableWorkflow) {
    // Carry the due occurrence so the started item links to it (drops off this initiator's due list).
    const extras = workflow.pendingOccurrenceId
      ? { queryParams: { occurrenceId: workflow.pendingOccurrenceId } }
      : {};
    this.router.navigate(['/workflow-runtime', 'definitions', workflow.workflowVersionId], extras);
  }

  openWorkflowItem(item: WorkflowRuntimeListItem, fromHistory = false) {
    // Activity history only shows when opened from the History section, not a live stage.
    this.router.navigate(['/workflow-runtime', 'instances', item.workflowItemId],
      fromHistory ? { queryParams: { view: 'history' } } : {});
  }

  statusLabel(item: WorkflowRuntimeListItem) {
    return item.derivedStatusLabel || item.statusLabel || item.status;
  }

  rowActionLabel(item: WorkflowRuntimeListItem) {
    if (this.isTerminalWorkflowItem(item)) return item.actionLabel || 'View Status';
    if (item.canResubmit) return item.actionLabel || 'Continue';
    if (item.canApprove || item.canReturn || item.canReject || item.canAct) return item.actionLabel || 'Take Action';
    return item.actionLabel || 'View Status';
  }

  isTerminalWorkflowItem(item: WorkflowRuntimeListItem) {
    const status = (item.status || '').toString().trim().toUpperCase();
    return status === 'COMPLETED' || status === 'REJECTED';
  }

  formatWorkflowDate(value?: string | null) {
    return value ? new Date(value).toLocaleString() : '';
  }

  ngOnDestroy(): void {
    this.persistRefreshState();
    if (this.historySearchTimer) clearTimeout(this.historySearchTimer);
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    if (this.filterPulseTimer) clearTimeout(this.filterPulseTimer);
    this.routerSubscription.unsubscribe();
    this.menuResetSubscription.unsubscribe();
  }

  //Load all log sheets
  retrieveAllLogSheets(reset?: boolean) {
    if (reset != true) {
      this.currentPage = 1;
    }
    
    const logSheetTypeParam = this.logSheetType == ScheduledType.none ? '' : this.enumName(ScheduledType, this.logSheetType);
    
    if (this.selectedStatuses.length > 1) {
      // Multiple statuses selected: make one API call per status and merge results
      const calls = this.selectedStatuses.map(status =>
        this.logSheetService.loadLogSheets(
          1, 100,
          logSheetTypeParam,
          status,
          this.startDate,
          this.endDate
        )
      );
      
      forkJoin(calls).subscribe({
        next: (responses: any[]) => {
          // Merge all results and deduplicate by logSheetId
          const seen = new Set<number>();
          let allLogs: any[] = [];
          
          for (const response of responses) {
            const items = response.items || response.Items || [];
            for (const log of items) {
              if (!seen.has(log.logSheetId)) {
                seen.add(log.logSheetId);
                allLogs.push(log);
              }
            }
          }
          
          // Client-side date filter to ensure correctness
          allLogs = this.filterByDateClientSide(allLogs);
          allLogs = this.filterByOperatorOwnership(allLogs);

          // Sort by most recent first
          allLogs.sort((a: any, b: any) => {
            const dateA = new Date(a.executionTime || a.lastModifiedDate).getTime();
            const dateB = new Date(b.executionTime || b.lastModifiedDate).getTime();
            return dateB - dateA;
          });
          
          // Client-side pagination
          this.totalPages = Math.ceil(allLogs.length / this.pageSize);
          this.loopArray = new Array(this.totalPages).fill(0).map((_, index) => index + 1);
          
          const startIndex = (this.currentPage - 1) * this.pageSize;
          const endIndex = startIndex + this.pageSize;
          this.logs = allLogs.slice(startIndex, endIndex);
          
          // Cache for pagination
          this.filteredLogs = allLogs;
        }
      });
    } else {
      // 0 or 1 status selected: use single API call with server pagination
      const statusParam = this.selectedStatuses.length === 1 ? this.selectedStatuses[0] : '';
      
      // When date range is active, fetch more and filter client-side for accuracy
      const needsClientDateFilter = this.startDate !== '' && this.endDate !== '';
      const fetchPage = needsClientDateFilter ? 1 : this.currentPage;
      const fetchSize = needsClientDateFilter ? 200 : this.pageSize;

      this.logSheetService.loadLogSheets(
        fetchPage,
        fetchSize,
        logSheetTypeParam,
        statusParam,
        this.startDate,
        this.endDate
      ).subscribe({
        next: (response: any) => {
          let items = response.items || response.Items || [];
          items = this.filterByOperatorOwnership(items);

          if (needsClientDateFilter) {
            items = this.filterByDateClientSide(items);
            this.totalPages = Math.ceil(items.length / this.pageSize);
            this.loopArray = new Array(this.totalPages).fill(0).map((_, index) => index + 1);
            this.filteredLogs = items;
            const startIndex = (this.currentPage - 1) * this.pageSize;
            this.logs = items.slice(startIndex, startIndex + this.pageSize);
          } else {
            this.filteredLogs = undefined;
            this.logs = items;
            this.totalPages = response.totalPages;
            this.loopArray = new Array(this.totalPages).fill(0).map((_, index) => index + 1);
          }
        }
      });
    }
  }

  handleFilter(scheduleType: ScheduledType) {
    if (this.logSheetType === scheduleType) {
      return;
    }
    this.logSheetType = scheduleType;
    this.currentPage = 1;
    // Clear any active search so it doesn't bleed across tab changes
    this.searchQuery = '';
    this.searchResults = null;
    this.isSearchOpen = false;
    this.allLogsCache = [];
    if (this.hasActiveFilters) {
      this.triggerFilterPulse();
    }
    this.persistRefreshState();
    this.retrieveAllLogSheets(true);
  }

  triggerFilterPulse() {
    if (this.filterPulseTimer) clearTimeout(this.filterPulseTimer);
    this.filterPulseActive = false;
    // Wait 3 seconds, then shake the button
    this.filterPulseTimer = setTimeout(() => {
      this.filterPulseActive = true;
      // Animation runs for 0.45s, then reset class
      setTimeout(() => {
        this.filterPulseActive = false;
      }, 450);
    }, 3000);
  }

  checkLogsheet(log: any) {
    if (this.checkExpire(log)) {
      this.showExpire(log);
      return;
    }

    this.logSheetService
      .retrieveActualLogsheet(log.logSheetId)
      .subscribe({
        next: (logsheet: any) => {
          const isBlocked =
            logsheet.status === 'inProgress' &&
            logsheet.logSheetTemplated?.scheduleType?.toLowerCase() !== 'ondemand';

          if (isBlocked) {
            this.showInProgressToast();
            return;
          }

		  this.router.navigate(
			['/form-details', logsheet.logSheetTemplateId],
			{ state: { logSheet: log }, queryParams: { logId: log.logSheetId } },
		  );
        },
        error: (err) => {
          console.error(err);
        },
      });
  }

  checkExpire(log: any) {
    return log.adminModificationIsExpired && log.status === 'executed'
  }

  showExpire(log: any) {
    if (log.adminModificationIsExpired && log.status === 'executed') {
      this.isExpired = true
    }
  }

  getStatusColor(status: string) {
    switch (status) {
      case 'inProgress':
        return 'inprogress'
      case 'executed':
        return 'executed'
      case 'overdue':
        return 'overdue'
      case 'available':
        return 'available'
      case 'Upcoming':
        return 'upcoming'
      case 'Incomplete':
        return 'incomplete'
      default:
        return 'status-none'
    }
  }

  showInProgressToast() {
    this.toastService.toastText = 'This Form is currently in progress!'
    this.toastService.isError = true
    this.toastService.showToast = true
    setTimeout(() => {
      this.toastService.showToast = false
    }, 5000)
  }

  //Navigate Pagination
  incrementArray(arr: any, increment: any) {
    return arr.map((item: any) => item + increment)
  }

  decrementArray(arr: any, decrement: any) {
    return arr.map((item: any) => item - decrement)
  }

  shiftPageRight() {
    const incrementedArray = this.incrementArray(
      this.loopArray,
      this.currentIncrement,
    )
    this.loopArray = incrementedArray
  }

  shiftPageLeft() {
    const decrementArray = this.decrementArray(
      this.loopArray,
      this.currentIncrement,
    )
    this.loopArray = decrementArray
  }

  setCurrentPage(page: any) {
    if (this.searchResults !== null) {
      this.searchPage = page;
      this.persistRefreshState();
      return;
    }
    this.currentPage = page;
    this.persistRefreshState();
    
    // If multi-status filtered results are cached, paginate from cache
    if (this.filteredLogs && this.filteredLogs.length > 0) {
      const startIndex = (this.currentPage - 1) * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      this.logs = this.filteredLogs.slice(startIndex, endIndex);
    } else {
      this.retrieveAllLogSheets(true);
    }
  }

  performIncrement() {
    if (this.activeCurrentPage === this.activeTotalPages) return;
    this.setCurrentPage(this.activeCurrentPage + 1);
    if (!this.searchResults && this.currentPage > this.loopArray[this.loopArray.length - 1]) {
      this.shiftPageRight();
    }
  }

  performDecrement() {
    if (this.activeCurrentPage === 1) return;
    this.setCurrentPage(this.activeCurrentPage - 1);
    if (!this.searchResults && this.currentPage < this.loopArray[0]) {
      this.shiftPageLeft();
    }
  }

  //Utility Piping Functions
  capitalize(string: string) {
    return this.titleCasePipe.transform(string)
  }

  stringToDate(date: string) {
    return this.datePipe.transform(date, 'MMM dd, yyyy')
  }
  stringToShortDate(date: string) {
    return this.datePipe.transform(date, 'MMM dd, yyyy')
  }
  stringToTime(date: string) {
    return this.datePipe.transform(date, 'h:mma')
  }

  convertToTitleCase(status: string) {
    return status.replace(/([A-Z])/g, ' $1').trim()
  }

  getStatusDisplayText(status: string): string {
    if (status === 'executed') {
      return 'Done';
    }
    return this.capitalize(this.convertToTitleCase(status));
  }

  enumName<T extends Record<string, string>>(enumObj: T, value: string): string {
    return Object.keys(enumObj).find(key => enumObj[key as keyof T] === value) ?? '';
  }

  toggleFilterOverlay() {
    this.displayFilterOverlay = !this.displayFilterOverlay;
  }

  toggleStatusSection() {
    this.statusSectionOpen = !this.statusSectionOpen;
    this.persistRefreshState();
  }

  clearAllFilters() {
    this.selectedStatuses = [];
    this.selectedDateRange = 'allTime';
    this.startDate = '';
    this.endDate = '';
    this.currentPage = 1;
    this.filterPulseActive = false;
    this.allLogsCache = [];
    if (this.searchQuery.trim()) {
      this.fetchAllLogsForSearch();
    }
    this.retrieveAllLogSheets(true);
    this.displayFilterOverlay = false;
    this.persistRefreshState();
  }

  toggleStatusFilter(status: Status) {
    const index = this.selectedStatuses.indexOf(status);
    if (index > -1) {
      this.selectedStatuses.splice(index, 1);
    } else {
      this.selectedStatuses.push(status);
    }
    this.persistRefreshState();
  }

  isStatusSelected(status: Status): boolean {
    return this.selectedStatuses.includes(status);
  }

  setDateRange(range: string) {
    this.selectedDateRange = range;
    const today = new Date();
    
    switch (range) {
      case 'today':
        this.startDate = this.formatDateForApi(today);
        this.endDate = this.formatDateForApi(today);
        break;
      case 'thisWeek':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        const endOfWeek = new Date(today);
        endOfWeek.setDate(today.getDate() + (6 - today.getDay()));
        this.startDate = this.formatDateForApi(startOfWeek);
        this.endDate = this.formatDateForApi(endOfWeek);
        break;
      case 'thisMonth':
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        this.startDate = this.formatDateForApi(startOfMonth);
        this.endDate = this.formatDateForApi(endOfMonth);
        break;
      case 'allTime':
      default:
        this.startDate = '';
        this.endDate = '';
        break;
    }
    this.persistRefreshState();
  }

  formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  filterByDateClientSide(logs: any[]): any[] {
    if (!this.startDate || !this.endDate) return logs;
    const start = new Date(this.startDate + 'T00:00:00');
    const end = new Date(this.endDate + 'T23:59:59');
    return logs.filter((log: any) => {
      const logDate = new Date(log.executionTime || log.lastModifiedDate);
      return logDate >= start && logDate <= end;
    });
  }

  filterByOperatorOwnership(items: any[]): any[] {
    if (!isOperator(this.role)) return items;
    // Standard Form Templates should be visible to all operators in the group
    if (this.logSheetType === ScheduledType.onDemandForm) return items;
    const currentUserLower = this.currentUser?.toLowerCase();
    if (!currentUserLower) return items;
    return items.filter((log: any) => {
      const scheduleType = log.logSheetTemplated?.scheduleType?.toLowerCase() ?? '';
      if (scheduleType !== 'ondemand') return true;
      // Template logsheets should remain visible to all operators in the group
      if (!log.isGeneratedOnDemandLogSheet) return true;
      // Spawned submissions: only show to the operator who submitted
      const executedEmail = log.executedByUserEmail?.toLowerCase();
      const currentUserField = log.currentUser?.toLowerCase();
      if (!executedEmail && !currentUserField) return true;
      return executedEmail === currentUserLower || currentUserField === currentUserLower;
    });
  }

  applyFilters() {
    this.currentPage = 1;
    this.allLogsCache = [];
    if (this.searchQuery.trim()) {
      this.fetchAllLogsForSearch();
    }
    this.retrieveAllLogSheets(true);
    this.displayFilterOverlay = false;
    this.persistRefreshState();
  }

  showTooltip(logId: string) {
    this.hoveredUsername = logId;
  }

  hideTooltip() {
    this.hoveredUsername = null;
  }

  toggleSearch() {
    this.isSearchOpen = !this.isSearchOpen;
    if (!this.isSearchOpen) {
      this.searchQuery = '';
      this.searchResults = null;
      this.allLogsCache = [];
      this.searchPage = 1;
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    }
    this.persistRefreshState();
    // No upfront fetch — cache loads lazily when the user types
  }

  fetchAllLogsForSearch() {
    const logSheetTypeParam = this.logSheetType === ScheduledType.none ? '' : this.enumName(ScheduledType, this.logSheetType);
    this.searchLoading = true;

    if (this.selectedStatuses.length > 1) {
      const calls = this.selectedStatuses.map(status =>
        this.logSheetService.loadLogSheets(1, 1000, logSheetTypeParam, status, this.startDate, this.endDate)
      );
      forkJoin(calls).subscribe({
        next: (responses: any[]) => {
          const seen = new Set<number>();
          let all: any[] = [];
          for (const response of responses) {
            const items = response.items || response.Items || [];
            for (const log of items) {
              if (!seen.has(log.logSheetId)) { seen.add(log.logSheetId); all.push(log); }
            }
          }
          this.allLogsCache = this.filterByOperatorOwnership(this.filterByDateClientSide(all));
          this.searchLoading = false;
          // Re-run search if user already typed something
          if (this.searchQuery.trim()) this.applySearchFilter(this.searchQuery.toLowerCase().trim());
        }
      });
    } else {
      const statusParam = this.selectedStatuses.length === 1 ? this.selectedStatuses[0] : '';
      this.logSheetService.loadLogSheets(1, 1000, logSheetTypeParam, statusParam, this.startDate, this.endDate)
        .subscribe({
          next: (response: any) => {
            let items = response.items || response.Items || [];
            items = this.filterByOperatorOwnership(items);
            if (this.startDate && this.endDate) items = this.filterByDateClientSide(items);
            this.allLogsCache = items;
            this.searchLoading = false;
            if (this.searchQuery.trim()) this.applySearchFilter(this.searchQuery.toLowerCase().trim());
          }
        });
    }
  }

  onSearchInput() {
    const query = this.searchQuery.toLowerCase().trim();
    this.persistRefreshState();
    if (!query) {
      this.searchResults = null;
      return;
    }
    // If cache is already populated, filter immediately
    if (this.allLogsCache.length > 0) {
      this.applySearchFilter(query);
      return;
    }
    // Cache is empty — debounce then fetch all, then filter
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => {
      this.fetchAllLogsForSearch();
    }, 300);
  }

  private applySearchFilter(query: string) {
    const source = this.allLogsCache.length > 0 ? this.allLogsCache : (this.filteredLogs ?? this.logs);
    this.searchResults = source.filter((log: any) => {
      const baseName = log.logSheetTemplated?.logSheetName ?? '';
      const fullName = log.versionNumber > 0 ? `${baseName} V${log.versionNumber}` : baseName;
      return fullName.toLowerCase().includes(query);
    });
    this.searchPage = 1;
  }

  private restoreRefreshState(): void {
    const saved = this.refreshState.load<NonAdminHomeRefreshState>('dashboard');
    if (!saved) return;

    if (saved.activeTab === 'workflows' || saved.activeTab === 'forms') this.activeTab = saved.activeTab;
    if (['tasks', 'start', 'history'].includes(saved.workflowFilter)) this.workflowFilter = saved.workflowFilter;
    if (['all', 'inprogress', 'completed', 'rejected'].includes(saved.historyStatusFilter)) {
      this.historyStatusFilter = saved.historyStatusFilter;
    }
    if (saved.workflowSort === 'newest' || saved.workflowSort === 'oldest') this.workflowSort = saved.workflowSort;
    if ((Object.values(ScheduledType) as ScheduledType[]).includes(saved.logSheetType)) {
      this.logSheetType = saved.logSheetType;
    }

    const validStatuses = new Set<Status>(Object.values(Status) as Status[]);
    this.selectedStatuses = (saved.selectedStatuses || []).filter(status => validStatuses.has(status));
    this.historySearch = saved.historySearch || '';
    this.workflowPage = Math.max(1, saved.workflowPage || 1);
    this.currentPage = Math.max(1, saved.currentPage || 1);
    this.selectedDateRange = saved.selectedDateRange || 'allTime';
    this.startDate = saved.startDate || '';
    this.endDate = saved.endDate || '';
    this.statusSectionOpen = saved.statusSectionOpen ?? true;
    this.isSearchOpen = Boolean(saved.isSearchOpen);
    this.searchQuery = saved.searchQuery || '';
    this.searchPage = Math.max(1, saved.searchPage || 1);
  }

  private resetToMenuHead(): void {
    if (this.historySearchTimer) clearTimeout(this.historySearchTimer);
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    if (this.filterPulseTimer) clearTimeout(this.filterPulseTimer);
    this.activeTab = 'workflows';
    this.workflowFilter = 'tasks';
    this.workflowSort = 'newest';
    this.workflowPage = 1;
    this.showHistory = false;
    this.historyItems = [];
    this.historySearch = '';
    this.historyStatusFilter = 'all';
    this.historyFilterOpen = false;
    this.currentPage = 1;
    this.logSheetType = ScheduledType.none;
    this.selectedStatuses = [];
    this.selectedDateRange = 'allTime';
    this.startDate = '';
    this.endDate = '';
    this.statusSectionOpen = true;
    this.displayFilterOverlay = false;
    this.filterPulseActive = false;
    this.isSearchOpen = false;
    this.searchQuery = '';
    this.searchResults = null;
    this.allLogsCache = [];
    this.filteredLogs = undefined;
    this.searchPage = 1;
    this.loadMyWork();
    this.retrieveAllLogSheets(true);
  }

  private persistRefreshState(): void {
    this.refreshState.save<NonAdminHomeRefreshState>('dashboard', {
      activeTab: this.activeTab,
      workflowFilter: this.workflowFilter,
      historySearch: this.historySearch,
      historyStatusFilter: this.historyStatusFilter,
      workflowSort: this.workflowSort,
      workflowPage: this.workflowPage,
      currentPage: this.currentPage,
      logSheetType: this.logSheetType,
      selectedStatuses: [...this.selectedStatuses],
      selectedDateRange: this.selectedDateRange,
      startDate: this.startDate,
      endDate: this.endDate,
      statusSectionOpen: this.statusSectionOpen,
      isSearchOpen: this.isSearchOpen,
      searchQuery: this.searchQuery,
      searchPage: this.searchPage,
    });
  }

}
